import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { change, Field } from 'redux-form';
import InputField from '../input/InputField';
import DropDownField from '../input/DropDownField';
import { CORPORATE, INDIVIDUAL, SIGNUP_ERROR_MSG } from '../../utils/constants';
import RadioField from '../input/RadioField';
import { normalizeAll, r_maxLength, r_onlyNumbers } from '../../utils/validation/restrictions';
import { dynamicMobileValidation, required } from '../../utils/validation';
import { FLOW_TYPE_ONBOARDING, SIGN_UP_START } from '../../utils/constants/eventsConstants';
import { logEvent } from '../../utils/events/logUserEvents';
import { LOG_TYPE } from '../../utils/events/eventsConstants';
import { HIDE_EYE_ICON, PASSWORD_GOOD_ICON, SHOW_EYE_ICON } from '@Images';
import { updateUserProperties } from '../../utils/events/cleverTap';

const GenerateField = ({
  fieldConfig,
  fieldValue,
  currentScreen,
  referralCode,
  disabledRadioField
}) => {
  let config = {};
  const {
    country_code,
    countries,
    dispatch,
    mobile_country_code,
    account_type,
    password,
    mobCountryCodes,
    mobile_number,
    email
  } = fieldValue;
  const [togglePasswordType, setTogglePasswordType] = useState(true);
  const [loggedGAEvent, setLoggedGAEvent] = useState(false);
  const [countryCodes, setCountryCode] = useState([]);
  const handleIconClick = () => {
    setTogglePasswordType(!togglePasswordType);
  };
  const malaysiaMobileCode = [{ key: '60', value: '60', text: '60' }];
  const singaporeMobileCode = [{ key: '65', value: '65', text: '65' }];
  useEffect(() => {
    getMobileCode(country_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country_code, account_type, mobCountryCodes]);

  const validatePassword = (password = '') => {
    const uppercaseError = !/(?=.*[A-Z])/g.test(password);
    const lowercaseError = !/(?=.*[a-z])/g.test(password);
    const numericError = !/[0-9]/g.test(password);
    const lengthError = password.length < 8;
    const specialCharErr = !/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~:{}|<>]/g.test(password);
    if (password.length !== 0) {
      if (!uppercaseError && !numericError && !lengthError && !specialCharErr && !lowercaseError) {
        return (
          <span
            style={{
              display: 'block',
              marginTop: '-23px'
            }}
          >
            <img src={PASSWORD_GOOD_ICON} style={{ marginRight: '5px' }} alt="good" />
            {"Now, that's a good password!"}
          </span>
        );
      } else {
        return (
          <>
            <span className={!lengthError ? 'line-through ' : 'err-msg'}>Min. 8 characters</span>,{' '}
            <span className={!uppercaseError ? 'line-through' : 'err-msg'}>one upper</span> &{' '}
            <span className={!lowercaseError ? 'line-through' : 'err-msg'}>lowercase</span>,{' '}
            <span className={!numericError ? 'line-through' : 'err-msg'}>one number</span>,{' '}
            <span className={!specialCharErr ? 'line-through' : 'err-msg'}>
              one special character
            </span>
          </>
        );
      }
    }
    return SIGNUP_ERROR_MSG;
  };

  const getMobileCode = async (countryCode) => {
    if (account_type === CORPORATE) {
      setCountryCode(mobCountryCodes);
    } else {
      if (countryCode) {
        switch (countryCode) {
          case 'SG':
            setCountryCode(singaporeMobileCode);
            break;
          case 'MY':
            setCountryCode(malaysiaMobileCode);
            break;
          default:
            break;
        }
      }
    }
  };

  const handleChange = (formName, key, value) => {
    dispatch(change(formName, key, value));
  };
  const handleEmailChange = (formName, key, value) => {
    dispatch(change(formName, key, value));
    if (!loggedGAEvent && currentScreen === 'signup') {
      logEvent(SIGN_UP_START, FLOW_TYPE_ONBOARDING, {}, LOG_TYPE.GA);
      setLoggedGAEvent(true);
    }
  };

  const handleEmailBlur = (event, value) => {
    if (value && currentScreen === 'signup') {
      updateUserProperties({ Email: value.toLowerCase() });
    }
  };

  const showMobileFields =
    (country_code === 'SG' || country_code === 'MY' || account_type === CORPORATE) &&
    countryCodes &&
    countryCodes.length !== 0;
  const isMobileOptional =
    ['HK', 'AU', 'SG'].includes(country_code) &&
    account_type === CORPORATE &&
    !mobile_country_code &&
    !mobile_number;
  const isAccountIndividual = account_type === INDIVIDUAL;
  switch (fieldConfig.name) {
    case 'account_type':
      config = {
        component: RadioField,
        disabled: disabledRadioField
      };
      break;
    case 'country_code':
      config = {
        component: DropDownField,
        bindToThisObject: country_code,
        label: account_type === INDIVIDUAL ? 'Country you stay in' : 'Country of registration',
        staticValues: countries,
        handleChange: handleChange,
        isSearchable: true,
        isTouched: fieldValue.country_code_error
      };
      break;
    case 'mobile_country_code':
      config = {
        component: showMobileFields ? DropDownField : null,
        bindToThisObject: mobile_country_code,
        handleChange: handleChange,
        iconStyles: { right: '-10px' },
        staticValues: countryCodes,
        dropDownStyle: { top: '65%' },
        preselect_and_disable_if_one_item: account_type === INDIVIDUAL,
        disabled: account_type === INDIVIDUAL,
        isSearchable: account_type === CORPORATE,
        code: true,
        isTouched: fieldValue.mobile_code_error,
        validate: isMobileOptional ? null : fieldConfig.validate
      };
      break;
    case 'mobile_number':
      config = {
        component: showMobileFields ? InputField : null,
        messageStyle: { bottom: '2px' },
        label: account_type === CORPORATE ? "Applicant's mobile number" : fieldConfig.label,
        validate:
          account_type === CORPORATE
            ? isMobileOptional
              ? null
              : [required, dynamicMobileValidation]
            : fieldConfig.validate,
        normalize:
          account_type === CORPORATE
            ? (() => {
                const restrictions = [],
                  { mobileValidationReducer } = fieldValue;
                if (mobileValidationReducer) {
                  if (mobileValidationReducer.type === 'startsWithFour')
                    restrictions.push(r_onlyNumbers);
                  if (mobileValidationReducer.length) {
                    const { max, exact } = mobileValidationReducer.length;
                    restrictions.push(r_maxLength(exact || max));
                  }
                  if (mobileValidationReducer.type === 'number') restrictions.push(r_onlyNumbers);
                  if (mobileValidationReducer.length) {
                    const { max, exact } = mobileValidationReducer.length;
                    restrictions.push(r_maxLength(exact || max || 50));
                  }
                }
                return normalizeAll(restrictions);
              })()
            : fieldConfig.normalize
      };
      break;
    case 'email':
      config = {
        component: InputField,
        bindToThisObject: email,
        onChange: handleEmailChange,
        onBlur: handleEmailBlur
      };
      break;
    case 'password':
      config = {
        component: InputField,
        icon: togglePasswordType ? SHOW_EYE_ICON : HIDE_EYE_ICON,
        type: togglePasswordType ? 'password' : 'text',
        onIconClick: handleIconClick,
        message:
          (currentScreen === 'signup' || currentScreen === 'resetPassword') &&
          validatePassword(password),
        label: currentScreen === 'resetPassword' ? 'New password' : fieldConfig.label,
        noErrMsg: currentScreen === 'signup'
      };
      break;
    case 'referred_by_referral_code':
      config = {
        component: isAccountIndividual ? InputField : null,
        disabled: referralCode,
        value: referralCode || ''
      };
      break;
    case 'registered_business_name':
      config = {
        component: account_type === CORPORATE ? InputField : null
      };
      break;
    default:
      config = { component: InputField };
      break;
  }
  return config.component && <Field {...fieldConfig} {...config} />;
};

GenerateField.propTypes = {
  fieldConfig: PropTypes.object,
  fieldValue: PropTypes.object,
  currentScreen: PropTypes.string,
  referralCode: PropTypes.string,
  disabledRadioField: PropTypes.bool
};

export default GenerateField;
